import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers';
import { useForm, useWatch } from 'react-hook-form';
import isDifferent from 'deep-diff';
import { useTranslation } from 'react-i18next';
import { SelectInput, IncrementableInput } from '@panel/commons';
import { useQuery, usePreventWindowUnload } from '@panel/commons/hooks';
import { currenciesBySite, site } from '@panel/commons/helpers';
import { Step1Module1Selectors, Step1Module1Operations } from '../../../ducks/step1Module1';
import { Step1Module4Selectors, Step1Module4Operations } from '../../../ducks/step1Module4';
import { WrapperContainer } from '../../Wrappers/WrapperContainer';
import { Step1Module4Schema } from './validations';
import { SavePostingOperations } from '../../../ducks/savePosting';
import { updateLastStep } from '../../../ducks/stepper';
import { module4BodyManager } from '../../../utils/bodyManager';
import { sendEvent, PUBLICATION_ABANDON, track } from '../../../utils/trackingEvent';
import {
  SubWrapper,
  ModuleTitle,
  ModuleSubTitle,
  FeatureContent,
  ContentDescription,
  FeatureLabel,
  InputWrapper,
  InputQuantity,
  PropertyDescription,
  InputTitle,
  InputDescription,
  ErrorMessage,
  IncrementableInputContainer,
  DescriptionCount,
  FooterDescriptionContainer
} from './styles';
import { PriceSection } from './priceSection';
import { useStepperCtx } from '../../stepper';
import { CustomCheckBox } from '../../commons/checkbox';
import { Step4Module1Operations } from '../../../ducks/step4Module1';

const siteId = site.id;

const mainFeatureFilters = [
  { name: 'bedrooms', id: 'CFT2' },
  { name: 'bathrooms', id: 'CFT3' },
  { name: 'toilettes', id: 'CFT4' },
  { name: 'garage', id: 'CFT7' }
];

const antiquityFilters = [
  { id: '0', name: 'CFT5', reference: 'optionNew' },
  { id: '1', name: 'CFT5', reference: 'optionYearsOld' },
  { id: '-1', name: 'CFT5', reference: 'optionUnderConstruction' }
];

const financialOptionFilter = [{ id: '1000001', name: 'credit' }];
const CreditOptionFilter = ['1', '2', '5', '11', '14', '26', '2001'];
const sitesWithRooms = ['ZPAR'];
if (sitesWithRooms.includes(siteId)) {
  mainFeatureFilters.unshift({ name: 'rooms', id: 'CFT1' });
}
const { separator } = Object.values(currenciesBySite[siteId])[0];

const Module4 = WrapperContainer(props => {
  const { setId, loading } = props;
  const [descriptionLength, setDescriptionLength] = useState(0);
  const { formValues, operationType, unitMeasurement, realEstateTypeId } = useSelector(
    Step1Module4Selectors.getStep1Module4State
  );
  const { operationTypeId, subPropertyTypeId, propertyTypes, propertySubTypes } = useSelector(
    Step1Module1Selectors.getInfoEvents
  );
  const selectorModulo1 = useSelector(Step1Module1Selectors.getStep1Module1State);
  const { setPreventTransition, nextStepperRoute } = useStepperCtx();
  const [postingId] = useQuery(useLocation(), ['postingId']);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formRef = useRef(null);

  const propertyTypeId = selectorModulo1.formValues && selectorModulo1.formValues.selectedPropertyTypeId;
  const siteWithWordingLand = site.isMexico || site.isPeru;
  const areaCoveredFilters =
    siteWithWordingLand && ['3', '20', '24', '26'].includes(propertyTypeId)
      ? [
          { name: 'covered', id: 'CFT101', key: 'covered' },
          { name: 'total', id: 'CFT100', key: 'landTotal' }
        ]
      : [
          { name: 'covered', id: 'CFT101', key: 'covered' },
          { name: 'total', id: 'CFT100', key: 'total' }
        ];

  const expensesMaxValue = formValues.CFT6 && formValues.CFT6.maxValue;
  const iptuMaxValue = formValues.CFT400 && formValues.CFT400.maxValue;

  const initialState = {
    CFT1: formValues.CFT1,
    CFT2: formValues.CFT2,
    CFT3: formValues.CFT3,
    CFT4: formValues.CFT4,
    CFT7: formValues.CFT7
  };

  const { register, handleSubmit, errors, setValue, watch, reset, control, formState } = useForm({
    defaultValues: formValues,
    resolver: yupResolver(Step1Module4Schema),
    context: { realEstateTypeId, expensesMaxValue, iptuMaxValue },
    mode: 'onBlur',
    shouldFocusError: true
  });

  const $antiquityId = watch('CFT5');
  const dataWatch = useWatch({ control, name: ['CFT1', 'CFT2', 'CFT3', 'CFT4', 'CFT7'] });

  const notifyAbandon = () => {
    if (selectorModulo1.postingStatuses.includes('DRAFT') && selectorModulo1.formValues.selectedOperationTypeId) {
      track(PUBLICATION_ABANDON, operationTypeId, postingId);
    }
  };

  useEffect(() => {
    window.addEventListener('beforeunload', notifyAbandon);
    return () => {
      window.removeEventListener('beforeunload', notifyAbandon);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectorModulo1.postingStatuses]);

  useEffect(() => {
    if (formState.isDirty || isDifferent(initialState, dataWatch) !== undefined) {
      setPreventTransition(formRef);
    } else {
      setPreventTransition(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.isDirty, dataWatch]);

  useEffect(() => {
    const formattedPrice = val => val && val.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${separator}`);

    if (formValues) {
      reset({
        ...formValues,
        CFT6: {
          value: formattedPrice(formValues.CFT6.value),
          value_unit: formValues.CFT6.value_unit
        },
        CFT400: {
          value: formattedPrice(formValues.CFT400.value),
          value_unit: formValues.CFT400.value_unit
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues, formValues.title, formValues.CFT5, formValues.price]);

  useEffect(() => {
    setId('formModule4');
  }, [setId]);

  useEffect(() => {
    if (postingId) {
      const payload = {
        module: 'FEATURE',
        step: 'STEP_MAIN',
        postingId
      };
      if (!propertyTypeId) {
        const payloadModulo1 = {
          module: 'OPERATION',
          step: 'STEP_MAIN',
          postingId
        };
        dispatch(Step1Module1Operations.getFormValues(payloadModulo1));
      }
      dispatch(Step1Module4Operations.getFormValues(payload));
      dispatch(Step4Module1Operations.getCredits(postingId));
    }
  }, [dispatch, postingId, propertyTypeId]);

  const onSubmit = (data, e) => {
    const payload = {
      body: module4BodyManager({ ...data, postingId, operationType }),
      step: 'STEP_MAIN',
      module: 'FEATURE'
    };
    const { nativeEvent } = e;
    const saveAndExit = nativeEvent.submitter && nativeEvent.submitter.id === 'saveAndExit';
    const savedFromModal = nativeEvent.detail && nativeEvent.detail.submitter === 'saveAndRedirect';
    const goBack = nativeEvent.detail && nativeEvent.detail.goBack;
    const path = goBack || nextStepperRoute || '/publicador/multimedia';

    const propertyObj = { list: propertyTypes, id: propertyTypeId };
    const subPropertyObj = { list: propertySubTypes, id: subPropertyTypeId };

    sendEvent(
      'Principales Modulo3',
      saveAndExit ? 'Guardar y salir' : 'Continuar',
      operationTypeId,
      propertyObj,
      subPropertyObj
    );
    if (saveAndExit) {
      notifyAbandon();
    }
    dispatch(SavePostingOperations.savePostingDB(payload, path, saveAndExit, savedFromModal));
    dispatch(updateLastStep('1', '3'));
  };

  const onKeyDown = e => {
    if (e.keyCode === 13) {
      const { form } = e.target;
      const index = Array.prototype.indexOf.call(form, e.target);
      if (
        form.elements[index + 1].type !== 'submit' &&
        !form.elements[index + 1].disabled &&
        !form.elements[index + 1].readOnly
      ) {
        form.elements[index + 1].focus();
      }
      if (form.elements[index].type !== 'textarea') {
        e.preventDefault();
      }
    }
  };

  const optionsByOperationType = useCallback(() => {
    if (siteId === 'ZPAR' && operationType) {
      if (operationType === '1' && CreditOptionFilter.includes(propertyTypeId)) {
        return financialOptionFilter;
      }
    }
    return null;
  }, [operationType, propertyTypeId]);

  const LengthCount = e => {
    setDescriptionLength(e.target.value.length);
  };

  usePreventWindowUnload(formState.isDirty);

  return (
    <SubWrapper>
      <ModuleTitle data-repa='Step1Modulo4-titlefeatures' className='withSubTitle'>
        {t('publisher.step1.module4.features.title')}
      </ModuleTitle>
      <ModuleSubTitle data-repa='Step1Modulo4-subTitle'>{t('publisher.step1.module4.features.text')}</ModuleSubTitle>
      <form
        data-repa='form-mainFeatures'
        id='formModule4'
        ref={formRef}
        onSubmit={handleSubmit(onSubmit)}
        onKeyDown={onKeyDown}
        autoComplete='off'
      >
        <fieldset disabled={loading} data-repa='form-mainFeatures-fieldset'>
          <FeatureContent>
            {mainFeatureFilters.map(feature => (
              <IncrementableInputContainer key={`featureKey-${feature.name}`}>
                <IncrementableInput
                  label={t(`publisher.step1.module4.features.${feature.name}`)}
                  data-repa={`form-mainFeatures-${feature.name}`}
                  name={feature.id}
                  ref={register}
                  minValue={0}
                  maxValue={99}
                  inputprops={{ 'data-repa': `form-mainFeatures-input-${feature.name}`, maxLength: 2 }}
                  setVal={setValue}
                />
              </IncrementableInputContainer>
            ))}
          </FeatureContent>
          <ModuleTitle data-repa='Step1Modulo4-title-area'>{t('publisher.step1.module4.area.title')}</ModuleTitle>
          <FeatureContent className='selectFeature'>
            {areaCoveredFilters.map(area => (
              <div className='errorWrapper' key={`featureAreakey-${area.name}`}>
                <SelectInput
                  ref={register}
                  selectId={`${area.id}.value_unit`}
                  inputId={`${area.id}.value`}
                  label={t(`publisher.step1.module4.area.${area.key}`)}
                  options={unitMeasurement}
                  data-repa={`form-mainFeatures-area-${area.name}`}
                  inputProps={{
                    'data-repa': `form-mainFeatures-area-${area.id}.value`,
                    maxLength: 7,
                    placeholder: '0'
                  }}
                />
                {errors && errors[area.id] && <ErrorMessage longText>{t(errors[area.id].value.message)}</ErrorMessage>}
              </div>
            ))}
          </FeatureContent>
          <ModuleTitle data-repa='Step1Modulo4-title-antiquity'>
            {t('publisher.step1.module4.antiquity.title')}
          </ModuleTitle>
          <FeatureContent>
            {antiquityFilters.map(({ id, name, reference }) => (
              <CustomCheckBox width='200px' isRadioButtom key={`check-${id}`}>
                <div>
                  <label htmlFor={`${reference}`}>
                    <input
                      key={`antiquityKey-${reference}`}
                      type='radio'
                      name={name}
                      ref={register}
                      value={id}
                      id={reference}
                      onChange={e => {
                        if (String(e.currentTarget.value) !== '1') {
                          setValue('numberOfYears', '');
                        } else {
                          setValue('numberOfYears', formValues.numberOfYears || '1');
                        }
                      }}
                      data-repa={`S1M4_antiquity_${id}`}
                    />
                    <span>{t(`publisher.step1.module4.antiquity.${reference}`)}</span>
                  </label>
                </div>
              </CustomCheckBox>
            ))}
            <InputWrapper>
              <InputQuantity
                className={errors.numberOfYears && 'error'}
                name='numberOfYears'
                disabled={$antiquityId !== '1'}
                maxLength={3}
                min={1}
                hidden={$antiquityId !== '1'}
                defaultValue={1}
                ref={register}
                data-repa='features-antiquityYears'
              />
              {errors.numberOfYears && (
                <ErrorMessage>{t('publisher.step1.module4.antiquity.optionYearsOld.validation')}</ErrorMessage>
              )}
            </InputWrapper>
          </FeatureContent>
          <ModuleTitle data-repa='Step1Modulo4-title-price'>{t('publisher.step1.module4.price.title')}</ModuleTitle>
          <FeatureContent className='selectFeature'>
            <PriceSection control={control} register={register} errors={errors} />
            {optionsByOperationType() &&
              optionsByOperationType().map(({ id, name }) => (
                <CustomCheckBox key={`check-${id}`}>
                  <div>
                    <label htmlFor={`${name}`} data-repa={`features-operationTypeOptions-${name}-label`}>
                      <input
                        type='checkbox'
                        name={id}
                        id={name}
                        ref={register}
                        data-repa={`features-operationTypeOptions-${name}`}
                      />
                      <span>{t(`publisher.step1.module4.price.options.${name}`)}</span>
                    </label>
                  </div>
                </CustomCheckBox>
              ))}
          </FeatureContent>
          <ModuleTitle data-repa='Step1Modulo4-title-propertyDesc' className='withSubTitle'>
            {t('publisher.step1.module4.propertyDescription.mainTitle')}
          </ModuleTitle>
          <ModuleSubTitle data-repa='Step1Modulo4-subtitle-desc'>
            {t('publisher.step1.module4.propertyDescription.mainText')}
          </ModuleSubTitle>
          <ContentDescription>
            <PropertyDescription>
              <FeatureLabel htmlFor='title'>
                {t('publisher.step1.module4.propertyDescription.title.title')}
              </FeatureLabel>
              <div className='titleContent'>
                <InputTitle
                  className={errors.title && 'error'}
                  type='text'
                  name='title'
                  ref={register}
                  placeholder={t('publisher.step1.module4.propertyDescription.title.placeholder')}
                  data-repa='S1M4_propertyTitle'
                  maxLength={80}
                />
                {errors.title && errors.title.type === 'required' && (
                  <ErrorMessage data-repa='Step1Modulo4-title-errorRequired'>
                    {t('publisher.step1.module4.propertyDescription.title.validationNotExist')}
                  </ErrorMessage>
                )}
                {errors.title && errors.title.type === 'min' && (
                  <ErrorMessage data-repa='Step1Modulo4-title-errorShort'>
                    {t('publisher.step1.module4.propertyDescription.title.validationTooShort')}
                  </ErrorMessage>
                )}
                {errors.title && errors.title.type === 'max' && (
                  <ErrorMessage data-repa='Step1Modulo4-title-errorLong'>
                    {t('publisher.step1.module4.propertyDescription.title.validationTooLong')}
                  </ErrorMessage>
                )}
              </div>
              <FeatureLabel htmlFor='description'>
                {t('publisher.step1.module4.propertyDescription.description.title')}
              </FeatureLabel>
              <InputDescription
                className={errors.description && 'error'}
                type='textarea'
                name='description'
                onChange={LengthCount}
                ref={register}
                placeholder={t('publisher.step1.module4.propertyDescription.description.placeholder')}
                data-repa='S1M4_propertyDescription'
              />
              <FooterDescriptionContainer error={errors.description}>
                {errors.description && errors.description.type === 'min' && (
                  <ErrorMessage data-repa='Step1Modulo4-description-errorShort'>
                    {t('publisher.step1.module4.propertyDescription.description.validationTooShort')}
                  </ErrorMessage>
                )}
                {errors.description && errors.description.type === 'max' && (
                  <ErrorMessage data-repa='Step1Modulo4-description-errorLong'>
                    {t('publisher.step1.module4.propertyDescription.description.validationTooLong')}
                  </ErrorMessage>
                )}
                <DescriptionCount>{descriptionLength}</DescriptionCount>
              </FooterDescriptionContainer>
            </PropertyDescription>
          </ContentDescription>
        </fieldset>
      </form>
    </SubWrapper>
  );
});

export { Module4 };
